import { useForm } from 'react-hook-form'
import * as z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Button } from '@/common/ui/button'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { Switch } from '@/common/ui/switch'
import { Editor } from '@/common/components/Editor'
import UploadImage from '@/common/components/UploadImage'
import useMutationGetBrokeNotificationUploadUrl from '@/core/api/hooks/useMutationGetBrokeNotificationUploadUrl'
import { BrokeNotificationModelType } from '@/schema'
import useQueryGetDepositPromotionNamesForBrokeNotification from '@/modules/promotion/api/hooks/useQueryGetDepositPromotionNamesForBrokeNotification'
import useQueryGetShopItems from '@/modules/shop/api/hooks/useQueryGetShopItems'
import useQueryGetCouponsForBrokeNotification from '@/modules/batches/api/hooks/useQueryGetCouponsForBrokeNotification'
import { TYPE_LABEL_MAP } from '../BrokeNotificationTable/constants'

const formSchema = z.object({
	title: z.string().min(1, 'กรุณากรอกหัวข้อ'),
	content: z.string().optional(),
	imageUrl: z.string().optional(),
	linkText: z.string().optional(),
	type: z.nativeEnum(BrokeNotificationModelType),
	depositPromotionId: z.string().optional(),
	shopItemId: z.string().optional(),
	couponCode: z.string().optional(),
	isActive: z.boolean().default(true),
})

export type BrokeNotificationFormValues = z.infer<typeof formSchema>

interface BrokeNotificationFormProps {
	defaultValues?: Partial<BrokeNotificationFormValues>
	onSubmit: (data: BrokeNotificationFormValues) => void
	submitButtonText: string
}

export default function BrokeNotificationForm({
	defaultValues = {
		title: '',
		content: '',
		imageUrl: '',
		linkText: '',
		type: BrokeNotificationModelType.RETURN_LOSS,
		depositPromotionId: '',
		shopItemId: '',
		couponCode: '',
		isActive: true,
	},
	onSubmit,
	submitButtonText,
}: BrokeNotificationFormProps) {
	const form = useForm<BrokeNotificationFormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: defaultValues as BrokeNotificationFormValues,
	})

	const { mutateAsync: getBrokeNotificationFileUrl } =
		useMutationGetBrokeNotificationUploadUrl()

	// Fetch data for selectors
	const { data: depositPromotions } =
		useQueryGetDepositPromotionNamesForBrokeNotification()
	const { data: shopItems } = useQueryGetShopItems({
		skip: 0,
		take: 1000,
		isActive: true,
	})
	const { data: coupons } = useQueryGetCouponsForBrokeNotification()

	const watchType = form.watch('type')

	const handleImageUploaded = (fileUrl: string) => {
		form.setValue('imageUrl', fileUrl)
	}

	return (
		<div className="max-h-[80vh] overflow-y-auto pr-2">
			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} className="mt-4 space-y-4">
					<FormField
						control={form.control}
						name="title"
						render={({ field }) => (
							<FormItem>
								<FormLabel>หัวข้อ</FormLabel>
								<FormControl>
									<Input {...field} placeholder="กรอกหัวข้อ" />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="content"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									เนื้อหา{' '}
									<span className="text-muted-foreground">(ไม่บังคับ)</span>
								</FormLabel>
								<FormControl>
									<Editor
										value={field.value || ''}
										onChange={field.onChange}
										placeholder="กรอกเนื้อหา"
										className="min-h-[200px] [&_.ql-container]:!min-h-[150px]"
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="imageUrl"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									รูปภาพแบนเนอร์{' '}
									<span className="text-muted-foreground">(ไม่บังคับ)</span>
								</FormLabel>
								<FormControl>
									<UploadImage
										onImageUploaded={handleImageUploaded}
										defaultValue={field.value || ''}
										getFileUrl={({ payload }) =>
											getBrokeNotificationFileUrl({
												fileExtension: payload.fileExtension,
											})
										}
										aspectRatio="16/9"
										previewHeight={200}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="type"
						render={({ field }) => (
							<FormItem>
								<FormLabel>ประเภท</FormLabel>
								<Select
									onValueChange={field.onChange}
									defaultValue={field.value}
								>
									<FormControl>
										<SelectTrigger>
											<SelectValue placeholder="เลือกประเภท" />
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										{Object.entries(TYPE_LABEL_MAP).map(([value, label]) => (
											<SelectItem key={value} value={value}>
												{label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="linkText"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									ข้อความปุ่มลิงก์{' '}
									<span className="text-muted-foreground">(ไม่บังคับ)</span>
								</FormLabel>
								<FormControl>
									<Input {...field} placeholder="เช่น ดูรายละเอียด" />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					{watchType === BrokeNotificationModelType.DEPOSIT_PROMOTION && (
						<FormField
							control={form.control}
							name="depositPromotionId"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										โปรโมชั่นฝากเงิน{' '}
										<span className="text-amber-600 dark:text-amber-400">
											(จำเป็น)
										</span>
									</FormLabel>
									<Select
										onValueChange={field.onChange}
										value={field.value || ''}
									>
										<FormControl>
											<SelectTrigger>
												<SelectValue placeholder="เลือกโปรโมชั่น" />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											{depositPromotions?.data?.map((promo) => (
												<SelectItem key={promo.id} value={promo.id}>
													{promo.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}

					{watchType === BrokeNotificationModelType.SHOP && (
						<FormField
							control={form.control}
							name="shopItemId"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										สินค้า{' '}
										<span className="text-amber-600 dark:text-amber-400">
											(จำเป็น)
										</span>
									</FormLabel>
									<Select
										onValueChange={field.onChange}
										value={field.value || ''}
									>
										<FormControl>
											<SelectTrigger>
												<SelectValue placeholder="เลือกสินค้า" />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											{shopItems?.data?.map((item) => (
												<SelectItem key={item.id} value={item.id}>
													{item.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}

					{watchType === BrokeNotificationModelType.COUPON && (
						<FormField
							control={form.control}
							name="couponCode"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										คูปอง{' '}
										<span className="text-amber-600 dark:text-amber-400">
											(จำเป็น)
										</span>
									</FormLabel>
									<Select
										onValueChange={field.onChange}
										value={field.value || ''}
									>
										<FormControl>
											<SelectTrigger>
												<SelectValue placeholder="เลือกคูปอง" />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											{coupons?.data?.map((coupon) => (
												<SelectItem key={coupon.code} value={coupon.code}>
													{coupon.code}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}

					<FormField
						control={form.control}
						name="isActive"
						render={({ field }) => (
							<FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
								<div className="space-y-0.5">
									<FormLabel className="text-base">สถานะการใช้งาน</FormLabel>
									<div className="text-sm text-muted-foreground">
										เปิด/ปิด การแสดงประกาศนี้
									</div>
								</div>
								<FormControl>
									<Switch
										checked={field.value}
										onCheckedChange={field.onChange}
									/>
								</FormControl>
							</FormItem>
						)}
					/>

					<Button type="submit" variant="default" className="w-full">
						{submitButtonText}
					</Button>
				</form>
			</Form>
		</div>
	)
}
